import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, BarChart3, Users, FileText, MessageSquare, Calendar, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../components/client-portal/clientPortalApi';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalReports() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const fetchAll = async () => {
        const endpoints = {
          overview: '/api/admin/reports/overview',
          stages: '/api/admin/reports/files-by-stage',
          conversions: '/api/admin/reports/conversions',
          avg: '/api/admin/reports/avg-stage-time',
          monthly: '/api/admin/reports/settlements-by-month?months=12',
          upcoming: '/api/admin/reports/upcoming-reviews',
        };
        const results = await Promise.all(Object.entries(endpoints).map(async ([k, p]) => {
          const r = await fetch(`${backendUrl}${p}`, { headers: auth });
          if (!r.ok) return [k, null];
          return [k, await r.json()];
        }));
        return Object.fromEntries(results);
      };
      try { setData(await fetchAll()); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const ov = data.overview || {};
  const conv = data.conversions || { funnel: [], total_files: 0 };
  const avg = data.avg || [];
  const stages = data.stages || [];
  const monthly = data.monthly || [];
  const upcoming = data.upcoming || [];

  return (
    <div className="min-h-screen bg-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-slate-900"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-2xl font-semibold text-slate-900">Client Portal Reports</h1>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="reports-overview">
          <Stat label="Active clients" value={ov.total_active_clients} icon={Users} />
          <Stat label="Active loan files" value={ov.active_loan_files} icon={Sparkles} />
          <Stat label="Outstanding docs" value={ov.outstanding_documents} icon={FileText} />
          <Stat label="Unread client msgs" value={ov.unread_client_messages} icon={MessageSquare} />
          <Stat label="Reviews next 90d" value={ov.upcoming_reviews_90d} icon={Calendar} />
          <Stat label="New PS requests" value={ov.new_post_settlement_requests} icon={BarChart3} />
        </div>

        <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Conversion funnel</h2>
          <div className="space-y-2">
            {conv.funnel.map((g) => {
              const pct = Math.round((g.rate_vs_total || 0) * 100);
              return (
                <div key={g.stage_key}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-700">{g.stage_label}</span>
                    <span className="text-slate-500"><span className="font-semibold text-slate-800">{g.count}</span> · {pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 mt-1">
                    <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-slate-500 mt-2">Total files: {conv.total_files}</div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 mb-3">Files by current stage</h2>
            {stages.length === 0 && <div className="text-sm text-slate-500">No data yet.</div>}
            <ul className="space-y-1.5">
              {stages.map((s) => (
                <li key={s.stage} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate mr-2">{s.stage}</span>
                  <span className="font-semibold text-slate-800">{s.count}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 mb-3">Average days per stage</h2>
            {avg.length === 0 && <div className="text-sm text-slate-500">No completed stages yet.</div>}
            <ul className="space-y-1.5">
              {avg.map((a) => (
                <li key={a.stage} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate mr-2">{a.stage}</span>
                  <span className="text-slate-800"><span className="font-semibold">{a.avg_days}</span> <span className="text-xs text-slate-500">days · {a.samples} sample{a.samples !== 1 ? 's' : ''}</span></span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Settlements per month</h2>
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 items-end h-32">
            {monthly.map((m) => {
              const max = Math.max(...monthly.map((x) => x.count), 1);
              const h = Math.round((m.count / max) * 100);
              return (
                <div key={m.month} className="flex flex-col items-center gap-1">
                  <div className="w-full rounded-t bg-emerald-500" style={{ height: `${h}%` }} />
                  <div className="text-[10px] text-slate-500">{m.month.slice(5)}</div>
                  <div className="text-[10px] text-slate-700 font-semibold">{m.count}</div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
          <h2 className="font-semibold text-slate-900 mb-3">Upcoming reviews (next 90 days)</h2>
          {upcoming.length === 0 ? (
            <div className="text-sm text-slate-500">No reviews scheduled in the next 90 days.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {upcoming.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center gap-3 flex-wrap text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800">{r.client_name || r.client_id}</div>
                    <div className="text-xs text-slate-500">{r.review_label}{r.notes ? ` · ${r.notes}` : ''}</div>
                  </div>
                  <span className="text-xs text-slate-700">{formatDate(r.due_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-semibold text-slate-900 mt-1">{value ?? 0}</div>
    </div>
  );
}
